import { useGetAlbumVideos } from '@vimeo/gctl-api/hooks/useAlbumVideos';
import { useViewer } from '@vimeo/viewer';
import { useBaseUrl } from '@vimeo/viewer-showcase-ui/hooks/use-base-url';
import { getEventIdFromClip } from '@vimeo/viewer-showcase-ui/utils/helper';

import { useGetFirstPlaylistVideo } from './use-get-first-playlist-video';
import { useGetShowcase } from './use-get-showcase';
import { useAlbumStore } from '../state/albumStore';
import { VIDEOS_API_VERSION } from '../utils/constants';
import { contextUrlParser } from '../utils/helper';

const DEFAULT_FEATURED_VIDEO_QUERY = {
  page: 1,
  perPage: 1,
  getFeatured: true,
  forceCollectionUri: true,
  sizes: ['1280x720'],
};

export const useGetFeaturedVideo = () => {
  const { albumId, password, isShowcaseEmbedded, referrer } = useAlbumStore((state) => ({
    albumId: state.albumId,
    password: state.hashedPass,
    isShowcaseEmbedded: state.isShowcaseEmbedded,
    referrer: state.referrer,
  }));

  const { layout, hideUpcoming, hasFeaturedContent } = useGetShowcase();
  const viewer = useViewer();
  const baseUrl = useBaseUrl(); // Use the custom hook for baseUrl

  const embedQuery = {
    isEmbed: isShowcaseEmbedded,
    referrer,
  };

  const isFeaturedVideoLayout = layout === 'live' || layout === 'featured';
  const {
    data: featuredVideoData,
    isLoading,
    ...gctlConfig
  } = useGetAlbumVideos(
    () => {
      // Only fetch the featured video if hasFeaturedContent is true or the layout is 'live' or 'featured'
      if (!viewer || !hasFeaturedContent || !isFeaturedVideoLayout) return null;
      return {
        where: { albumId },
        select: [
          'badge.type',
          'description',
          'descriptionHtml',
          'link',
          'live', // TODO: get all live data for now, eventually we need less than this
          'name',
          'pictures.sizes.link',
          'pictures.sizes.width',
          'pictures.uri',
          'privacy.download',
          'privacy.view',
          'uri',
        ],
        query: {
          ...DEFAULT_FEATURED_VIDEO_QUERY,
          ...embedQuery,
          ...(password && { password }),
          ...(hideUpcoming && { filter: 'playable' }),
        },
        headers: { Accept: `application/vnd.vimeo.*+json;version=${VIDEOS_API_VERSION}` },
      };
    },
    {
      revalidateOnFocus: false,
    },
  );

  const shouldGetFirstVideo = isFeaturedVideoLayout && !isLoading && !featuredVideoData?.data?.[0];
  const { firstClip, isLoading: isFirstClipLoading } =
    useGetFirstPlaylistVideo(shouldGetFirstVideo);

  const clip = featuredVideoData?.data?.[0] ?? firstClip; // Default to first clip if no featured video is found to have legacy compatibility
  let eventId;
  if (clip?.live?.status) {
    eventId = getEventIdFromClip(clip); // live clip finished archive (transcoding) process
  }

  return {
    featuredVideo: clip
      ? {
          ...clip,
          thumbnailUrl: clip?.pictures?.sizes?.[clip?.pictures?.sizes.length - 1]?.link,
          ...(clip?.link && contextUrlParser(clip.link, { baseUrl, eventId })),
          ...(eventId && { eventId }),
          isLiveClip: !!clip?.live,
        }
      : null,
    ...gctlConfig,
    isFeaturedOrFirstVideoLoading: isLoading || isFirstClipLoading,
  };
};
